import { CommonModule } from '@angular/common';
import { Component, model, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { ApiService } from '../services/api.service';
import { MatButtonModule } from '@angular/material/button';
import { User, CHURCH_IDS } from '../constants/constants';
import { catchError, EMPTY } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { NotificationService } from '../services/notification.service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { UserConfirmationDialogComponent } from './user-confirmation-dialog/user-confirmation-dialog.component';

@Component({
    selector: 'app-user-management',
    imports: [MatIconModule, MatButtonModule, MatInputModule, MatTableModule, MatSelectModule,
        ReactiveFormsModule, CommonModule, MatCheckboxModule, MatDialogModule],
    templateUrl: './user-management.component.html',
    styleUrl: './user-management.component.scss'
})
export class UserManagementComponent implements OnInit {
  userForm: FormGroup;
  roles: string[] = ['admin', 'editor', 'viewer'];
  churchIds: string[] = CHURCH_IDS;
  users: User[] = [];
  editIndex: number | null = null;
  showPassword: boolean = false;
  readonly checked = model(false);

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private toastr: NotificationService,
    private dialog: MatDialog
  ) {
    this.initForm();
  }

  private initForm(isEdit: boolean = false) {
    const passwordValidators = isEdit ? [] : [Validators.required, Validators.minLength(6)];
    this.userForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', passwordValidators],
      userrole: ['', Validators.required],
      church_id: ['', Validators.required],
      account_locked: [false]
    });
  }

  ngOnInit(): void {
    this.fetchAllUsers();
  }

  fetchAllUsers(){
    this.apiService.getAllUsers().subscribe(users=>this.users=users)
  }

  addOrUpdateUser(): void {
    if (this.userForm.invalid) return;

    const formValue = this.userForm.value;
    const user: User = {
      ...formValue,
      // Only include password if it's not empty
      ...(formValue.password ? { password: formValue.password } : {})
    };

    const dialogRef = this.dialog.open(UserConfirmationDialogComponent, {
      width: '400px',
      data: {
        user,
        isEdit: this.editIndex !== null,
        passwordChanged: Boolean(formValue.password)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (this.editIndex !== null) {
          this.updateUser(user);
        } else {
          this.addUser(user);
        }
      }
    });
  }

  private updateUser(user: User): void {
    this.apiService.updateUser(user).pipe(catchError(resp=>{
      this.toastr.error(resp?.error?.message ||"User updating failed");
      return EMPTY;
    })).subscribe((resp: any)=>{
      this.toastr.success(resp?.message ||"User updated successfully");
      this.fetchAllUsers();
      this.resetForm();
    });
  }

  private addUser(user: User): void {
    this.apiService.register(user).pipe(catchError(resp=>{
      this.toastr.error(resp?.error?.message ||"User addition failed");
      return EMPTY;
    })).subscribe((resp:any)=>{
      this.toastr.success(resp?.message ||"User added successfully");
      this.fetchAllUsers();
      this.resetForm();
    });
  }

  editUser(index: number): void {
    this.editIndex = index;
    const user = this.users[index];
    this.initForm(true); // Initialize form with edit mode
    this.userForm.patchValue({
      username: user.username,
      userrole: user.userrole,
      church_id: user.church_id,
      account_locked: user.account_locked || false,
      password: '' // Clear password field when editing
    });
  }

  resetForm(){
    this.editIndex = null;
    this.initForm(); // Reset form to add mode
    this.userForm.reset();
  }

  deleteUser(user: User): void {
    const dialogRef = this.dialog.open(UserConfirmationDialogComponent, {
      width: '400px',
      data: { user, isEdit: false, isDelete: true }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.apiService.deleteUser(user.username).pipe(catchError(resp=>{
          this.toastr.error(resp?.error?.message ||"User deletion failed");
          return EMPTY;
        })).subscribe((resp:any)=>{
          this.toastr.success(resp?.message ||"User deleted successfully");
          this.fetchAllUsers();
        });
      }
    });
  }
}
